import { Box, Card, Divider, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lightPrimary } from 'src/colors';
import { formatDate } from 'src/components/atoms/format-date';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { Booking } from 'src/features/bookings/api/types/response/booking.response';
import { useEmployeesGroupsByTokens } from 'src/features/employees-groups/api/hooks/use-employees-groups-by-tokens';
import { useEmployeesByTokens } from 'src/features/employees/api/hooks/use-employees-by-tokens';

interface Props extends DialogActions {
  cancelledBookings: Booking[];
}

export const CancelledBookingsSummaryDialog = ({
  open,
  onCancel,
  cancelledBookings,
}: Props) => {
  const { t } = useTranslation(['employees', 'common']);

  const [bookings, setBookings] = useState<Booking[]>(cancelledBookings);

  //Persist the bookings to manage in the dialog even after canceling them
  useEffect(() => {
    if (open) {
      setBookings(cancelledBookings);
    }
  }, [open]);

  const employeesTokens = bookings.map(
    ({ employeeBookerToken }) => employeeBookerToken,
  );

  const { results: employees } = useEmployeesByTokens(employeesTokens);

  const employeesGroupsTokens =
    (employees?.hits
      ?.map(({ employeeGroupToken }) => employeeGroupToken)
      .filter(Boolean) as string[]) ?? [];

  const { results: employeesGroups } = useEmployeesGroupsByTokens(
    employeesGroupsTokens,
  );

  const activeBookingsCancelled = bookings.filter(
    booking => booking.from <= Date.now() && booking.to >= Date.now(),
  );

  const futureBookingsCancelled = bookings.filter(
    booking => booking.from > Date.now(),
  );

  return (
    <Dialog
      title={t('employees:employees.cancelledBookingsSummaryDialog.title')}
      open={open}
      onCancel={onCancel}
      cancelButtonText={t('common:button.ok').toUpperCase()}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2">
          {t('employees:employees.cancelledBookingsSummaryDialog.description', {
            groupsNames: employeesGroups?.hits
              ?.map(({ name }) => name)
              .join(', '),
          })}
        </Typography>

        <Card
          variant="outlined"
          sx={{ px: 2, py: 1, maxHeight: 220, overflowY: 'auto' }}>
          <Typography
            variant="body2"
            color={lightPrimary.borders}
            fontWeight={500}
            sx={{ mb: 2 }}>
            {t(
              'employees:employees.cancelledBookingsSummaryDialog.activeBookingsCancelled',
            )}
          </Typography>

          {activeBookingsCancelled.length > 0 ? (
            activeBookingsCancelled.map((booking, index) => (
              <Box key={index}>
                <Typography variant="body2">
                  {booking.employeeBookerEmail}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {`${formatDate(new Date(booking.from), 'd MMM, HH:mm')} - ${formatDate(new Date(booking.to), 'HH:mm')}`}
                </Typography>

                {index < activeBookingsCancelled.length - 1 ? (
                  <Divider sx={{ my: 1 }} />
                ) : null}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              {t(
                'employees:employees.cancelledBookingsSummaryDialog.noBookingsCancelled',
              )}
            </Typography>
          )}
        </Card>

        <Card
          variant="outlined"
          sx={{ px: 2, py: 1, maxHeight: 220, overflowY: 'auto' }}>
          <Typography
            variant="body2"
            color={lightPrimary.borders}
            fontWeight={500}
            sx={{ mb: 2 }}>
            {t(
              'employees:employees.cancelledBookingsSummaryDialog.futureBookingsCancelled',
            )}
          </Typography>

          {futureBookingsCancelled.length > 0 ? (
            futureBookingsCancelled.map((booking, index) => (
              <Box key={index}>
                <Typography variant="body2">
                  {booking.employeeBookerEmail}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  {`${formatDate(new Date(booking.from), 'd MMM, HH:mm')} - ${formatDate(new Date(booking.to), 'HH:mm')}`}
                </Typography>

                {index < futureBookingsCancelled.length - 1 ? (
                  <Divider sx={{ my: 1 }} />
                ) : null}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary">
              {t(
                'employees:employees.cancelledBookingsSummaryDialog.noBookingsCancelled',
              )}
            </Typography>
          )}
        </Card>
      </Box>
    </Dialog>
  );
};
